import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { DynamicFormModel, DynamicFormService, DynamicSelectModel } from '@ng-dynamic-forms/core';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  TranslatePipe,
  TranslationService,
} from '@spartacus/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, mergeMap, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { ShimanoBusinessTypeService } from 'src/app/service/businessType/shimano-business-type.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import {
  GetCountryInfoPayload,
  GetCountryUserPayload,
  GetLanguageUserPayload,
  GetStateUserPayload,
  RequestAccountPayload,
  RequestAccountService,
} from '../../../../service/user/request-account.service';
import { DynamicFormsComponent } from '../../dynamic-forms/dynamic-forms.component';

@Component({
  selector: 'app-request-account',
  templateUrl: './request-account.component.html',
  styleUrls: ['./request-account.component.scss'],
  providers: [AsyncPipe],
  imports: [RouterLink, DynamicFormsComponent, NgClass, TranslatePipe],
})
export class RequestAccountComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild(DynamicFormsComponent, { static: false }) form: DynamicFormsComponent;
  @ViewChild(DynamicFormsComponent, { static: false }) formGroupEmit: DynamicFormsComponent;

  requestAccount$: Observable<any>;
  status: string;
  formGroupControls: any;
  success = false;
  hasError = false;
  recaptchaError = false;
  cssUrl: string;
  requestAccount: boolean;
  formGroup: FormGroup;
  formModel: DynamicFormModel;
  valid = false;
  globalMsg = document.querySelector('cx-global-message');
  imgBanner = document.querySelector('.LeftContentSlot img');
  rightContentSlot = document.querySelector('.RightContentSlot');
  hasState = 'false';
  errorMessages: any[] = [];
  initCountrySelected: string;
  initBusinessTypeSelected: string;

  selectAccountTypeModel: DynamicSelectModel<string>;
  selectCountryModel: DynamicSelectModel<string>;
  selectLanguageModel: DynamicSelectModel<string>;
  selectStateModel: DynamicSelectModel<string>;
  selectCompanyCode: DynamicSelectModel<string>;

  selectBusinessTypeModelChanged$ = new BehaviorSubject<boolean>(false);
  selectCountryModelChanged$ = new BehaviorSubject<boolean>(false);
  selectLanguageModelChanged$ = new BehaviorSubject<boolean>(false);
  firstOfLanguageItems: string;

  selectCompanyCode$ = new BehaviorSubject<boolean>(false);
  companyCodeSelected: string;

  currentPageSubscription$: Subscription;

  formError = false;
  prop: any;

  // tslint:disable-next-line: max-line-length
  constructor(
    private formService: DynamicFormService,
    private requestAccountService: RequestAccountService,
    public sanitizer: DomSanitizer,
    private globalMessageService: GlobalMessageService,
    private componentService: ComponentService,
    private translation: TranslationService,
    private cmsService: CmsService,
    private businessTypeService: ShimanoBusinessTypeService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private pipeAsync: AsyncPipe,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.requestAccount = this.f === 'true';
    if (this.requestAccount) {
      this.currentPageSubscription$ = this.cmsService
        .getCurrentPage()
        .pipe(
          switchMap((currentPage) => {
            let requestAccountFormModel$: Observable<any>;
            if (currentPage && currentPage.template === 'LoginPageTemplate') {
              requestAccountFormModel$ = this.componentService.dynamicFormModel$;
            }
            return requestAccountFormModel$;
          }),
          tap((formModel) => {
            if (formModel) {
              this.updateFormModelDynamic(formModel);
              this.storageService.removeLocalStorageItem('requestAccountDetails');
            }
          }),
          switchMap(() => this.businessTypeService.getActive()),
          tap((activeBusinessType) => {
            this.selectAccountTypeModel.value = activeBusinessType.toUpperCase();
            if (!this.selectBusinessTypeModelChanged$.getValue()) {
              this.initBusinessTypeSelected = this.storageService.getSessionStorageItem('businessTypeSelected');
            }
          }),
          switchMap(() => this.selectAccountTypeModel.valueChanges),
          tap((businessType) => {
            this.selectBusinessTypeModelChanged$.next(this.initBusinessTypeSelected !== businessType);
          }),
          switchMap(() => this.getCountryItems$(this.selectAccountTypeModel.value)),
          tap((countries) => {
            if (countries.items && countries.items.length) {
              this.checkPriority(countries.items, this.selectCountryModel.value, 'countrySelected');
              this.selectCountryModel.options = this.setSelectModelOptions(countries);
              if (!this.selectCountryModelChanged$.getValue()) {
                this.selectCountryModel.value = this.storageService.getSessionStorageItem('countrySelected');
                this.initCountrySelected = this.selectCountryModel.value;
              }
            }
          }),
          switchMap(() => this.selectCountryModel.valueChanges),
          tap((country) => {
            this.countryService.setActive((country as string).toLowerCase());
            this.storageService.setLocalStorageitem('countryCode', country as string);
            this.componentService.countryCodeForRequestAccount$.next(country as string);
            this.selectCountryModelChanged$.next(this.initCountrySelected !== this.selectCountryModel.value);
          }),
          switchMap((country) => this.getStateForRequestAccount$(country)),
          tap((states) => {
            if (states.items && states.items.length && this.selectStateModel) {
              this.selectStateModel.options = this.setSelectModelOptions(states);
            }
          }),
          switchMap(() => this.getCountryInfo$(this.selectCountryModel.value)),
          tap((countryInfo) => {
            if (countryInfo && countryInfo.extraProperties) {
              const mobilePrefix = countryInfo.extraProperties.entry.filter((i: any) => i.key === 'mobilePrefix')[0];
              this.componentService.mobilePrefixInput$.next(mobilePrefix.value);
              this.storageService.setLocalStorageitem('mobilePrefix', mobilePrefix.value);
            }
          }),
          switchMap(() => this.getLanguageItems$(this.selectAccountTypeModel.value, this.selectCountryModel.value)),
          tap((languages) => {
            if (languages.items && languages.items.length) {
              this.checkPriority(languages.items, this.selectLanguageModel.value, 'language');
              this.selectLanguageModel.options = this.setSelectModelOptions(languages);
              if (!this.selectLanguageModelChanged$.getValue()) {
                this.selectLanguageModel.value = this.storageService.getSessionStorageItem('language');
                this.firstOfLanguageItems = this.selectLanguageModel.options[0].value;
              }
              if (this.selectCountryModelChanged$.getValue()) {
                this.storageService.setLocalStorageitem('requestAccountDetails', JSON.stringify(this.formGroup.value));
                this.languageService.setActive(this.firstOfLanguageItems);
                window.location.reload();
              }
              if (this.selectBusinessTypeModelChanged$.getValue()) {
                this.businessTypeService.setActive((this.selectAccountTypeModel.value as string).toLowerCase());
                window.location.reload();
              }
            }
          }),
          switchMap(() => this.selectLanguageModel.valueChanges.pipe(pairwise())),
          tap(([prevLanguage, nextLanguage]) => {
            if (prevLanguage !== nextLanguage) {
              if (this.formGroup) {
                this.storageService.setLocalStorageitem('requestAccountDetails', JSON.stringify(this.formGroup.value));
              }
              this.languageService.setActive(nextLanguage);
              window.location.reload();
            }
          }),
          switchMap(() => this.componentService.dynamicFormGroup$),
          tap((formGroup) => {
            if (formGroup) {
              this.updateFormGroup(formGroup);
            }
          })
        )
        .subscribe();
    }
  }

  checkPriority(lists: any, selected: any, session: any) {
    if (
      lists.filter((item: any) => item.code === selected).length <= 0 &&
      this.selectAccountTypeModel.value !==
        this.storageService.getSessionStorageItem('businessTypeSelected').toUpperCase()
    ) {
      let priority = 0;
      lists.forEach((prop: any) => {
        const currentCode = prop.code;
        prop.extraProperties.entry.forEach((prio: any) => {
          if (prio.key === 'priority') {
            if (parseInt(prio.value) > priority) {
              priority = parseInt(prio.value);
              this.storageService.setSessionStorageItem(session, currentCode);
              return;
            }
          }
        });
      });
    }
  }

  setSelectModelOptions(optionsData: any): any {
    const selectModelOptions: any = [];
    for (const item of optionsData.items) {
      const options = {
        label: item.name,
        value: item.code,
      };
      selectModelOptions.push(options);
    }
    return selectModelOptions;
  }

  ngAfterViewInit() {
    if (this.requestAccount) {
      setTimeout(() => {
        if (typeof grecaptcha !== 'undefined' || grecaptcha !== null) {
          if (grecaptcha && grecaptcha.getResponse().length > 0) {
            this.recaptchaError = false;
          }
        }
      }, 5000);
    }
  }

  ngAfterViewChecked() {
    const offSetHeight = 78;

    if (!this.imgBanner) {
      this.imgBanner = document.querySelector('.LeftContentSlot img');
    }
    if (this.requestAccount) {
      if (this.imgBanner !== null) {
        this.imgBanner.classList.add('request-acct-image');
      }
    } else {
      if (this.imgBanner !== null) {
        this.imgBanner.classList.remove('request-acct-image');
      }
    }
    this.hasState = this.storageService.getLocalStorageItem('state');
    this.cdr.detectChanges();
  }

  private postRequestAccount(form: any): Observable<any> {
    const requestAccountPayload3: RequestAccountPayload = {
      uid:
        String(form.accountType).toLowerCase() +
        ':' +
        String(form.regionConfigCountry).toLowerCase() +
        ':' +
        form.emailAddress_full,
      businessType: form.accountType,
      countryIsoCode: form.regionConfigCountry,
      language: form.language,
      firstName: form.firstName_full,
      lastName: form.lastName_full,
      registrationNumber: form.companyRegistrationNumber,
      companyName: form.companyName ? form.companyName : form.storeName,
      line1: form.addressLine1 ? form.addressLine1 : form.address,
      line2: form.addressLine2,
      email: form.emailAddress_full,
      town: form.city,
      regionIsoCode: form.state,
      postalCode: form.postalCode ? form.postalCode : form.postalCode_twelveLim,
      prefix: this.mobilePrefix,
      cellphone: form.contactNumber ? form.contactNumber : form.phoneNumber,
      companyCode: form.companyCode,
      representativeName: form.representativeName,
      corporateName: form.corporateName,
      shopURL: form.shopURL,
      yearsInBusiness: form.yearsInBusiness,
      mainCustomer: form.mainCustomer,
      fax: form.faxNumber,
    };
    return this.requestAccountService.postRequestAccount(requestAccountPayload3);
  }

  onClickSubmit($event: any) {
    this.globalMsg.setAttribute('style', 'display:block;');
    this.recaptchaError = false;
    if (grecaptcha.getResponse().length === 0) {
      this.recaptchaError = true;
      return;
    }
    this.postRequestAccount(this.form.formGroup.value)
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (typeof res !== 'undefined') {
            if (res.type === 'userWsDTO') {
              this.componentService.setrequestSuccessSubject(true);
              this.requestAccount = false;
              this.success = true;
              this.hasError = false;
              const keysToRemove = ['accountType', 'countryCode', 'hasRequested', 'mobilePrefix'];
              for (const key of keysToRemove) {
                this.storageService.removeLocalStorageItem(key);
              }
              // this.returnToLogin();
              window.scroll(0, 0);
              this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
              this.formService.detectChanges();
            } else {
              this.hasError = true;
            }
          } else {
            this.hasError = true;
          }
          if (this.hasError) {
            this.globalMessageService.add(
              'There was an error with your request. Please try again later.',
              GlobalMessageType.MSG_TYPE_ERROR
            );
          }
        },
        (err) => {
          this.globalMessageService.add(err.error.errors[0].message, GlobalMessageType.MSG_TYPE_ERROR);
        }
      );
  }

  get mobilePrefix() {
    return this.storageService.getLocalStorageItem('mobilePrefix');
  }

  get f() {
    return this.storageService.getLocalStorageItem('hasRequested');
  }

  getCountryItems$(accountType: string | string[]): Observable<any> {
    const getCountriesPayload: GetCountryUserPayload = {
      fields: 'DEFAULT',
      accountType,
    };
    return this.requestAccountService.getCountriesForRequestAccount(getCountriesPayload);
  }

  getLanguageItems$(businessType: string | string[], countryIsoCode: string | string[]): Observable<any> {
    const getLanguageUserPayload: GetLanguageUserPayload = {
      fields: 'DEFAULT',
      businessType,
      countryIsoCode,
    };
    return this.requestAccountService.getLanguageForRequestAccount(getLanguageUserPayload);
  }

  getCountryInfo$(countryIsoCode: string | string[]): Observable<any> {
    const getCountryInfoPayload: GetCountryInfoPayload = {
      fields: 'DEFAULT',
      countryIsoCode,
    };
    return this.requestAccountService.getCountryInfo(getCountryInfoPayload);
  }

  getStateForRequestAccount$(countryIsoCode: string | string[]): Observable<any> {
    const getStateUserPayload: GetStateUserPayload = {
      fields: 'DEFAULT',
      countryIsoCode,
    };
    return this.requestAccountService.getStateForRequestAccount(getStateUserPayload);
  }

  getTranslatedError(id: string): Observable<string> {
    return this.taxLabel$.pipe(
      mergeMap((taxLabel) =>
        this.translation.translate(
          id === 'companyRegistrationNumber' ? `error.dynamicform.${id}.` + taxLabel : `error.dynamicform.${id}`
        )
      )
    );
  }

  updateFormGroup(form: any): void {
    this.formGroup = form;
  }

  updateFormModelDynamic(form: any) {
    this.formModel = form;
    if (this.formModel.length > 0) {
      const formModel = this.formModel;
      form.forEach((field: any) => {
        this.prop = this.formService.findModelById<any>(field.id, formModel);

        // set required for firstName_full & lastName_full to true
        if (this.prop.id === 'firstName_full' || this.prop.id === 'lastName_full') {
          field.required = true;
        }

        if (this.prop !== null) {
          if (
            (field.required && (this.prop.value === null || this.prop.value === '')) ||
            field.pattern ||
            this.hasState === 'true'
          ) {
            let hasError;
            if (field.pattern) {
              hasError = {
                required: this.pipeAsync.transform(this.getTranslatedError(this.prop.id)),
                pattern: this.pipeAsync.transform(this.getTranslatedError(this.prop.id + '_valid')),
              };
            } else {
              hasError = {
                required: this.pipeAsync.transform(this.getTranslatedError(this.prop.id)),
              };
            }
            this.formError = true;
            this.prop.errorMessages = hasError;
          }

          // For the DropdownFields
          if (this.prop.id === 'accountType') {
            this.selectAccountTypeModel = this.prop;
          } else if (this.prop.id === 'regionConfigCountry') {
            this.selectCountryModel = this.prop;
          } else if (this.prop.id === 'language') {
            this.selectLanguageModel = this.prop;
          } else if (this.prop.id === 'state') {
            this.selectStateModel = this.prop;
          } else if (this.prop.id === 'companyCode') {
            this.selectCompanyCode = this.prop;
            this.selectCompanyCode.valueChanges.subscribe((data) => {
              if (data === 'true' || data === 'false') {
                this.selectCompanyCode$.next(this.companyCodeSelected !== this.selectCompanyCode.value);
              } else {
                if (!this.selectCompanyCode$.getValue()) {
                  this.selectCompanyCode.value = 'false';
                  this.companyCodeSelected = this.selectCompanyCode.value;
                }
              }
            });
          }
        }
      });
      this.formService.detectChanges();
    }
  }

  returnToLogin(): void {
    this.requestAccount = false;
    this.storageService.setLocalStorageitem('hasRequested', 'false');
    this.storageService.setLocalStorageitem('pageChange', 'false');
    this.router.navigate(['/login']);
    window.location.reload();
  }

  get taxLabel$(): Observable<string> {
    return this.countryService.getActive().pipe(
      take(1),
      map((activeCountry) => (activeCountry === 'sg' ? activeCountry : 'default'))
    );
  }

  getOS(): string {
    var OSName = '';
    if (navigator.platform.toLowerCase().indexOf('macos') != -1) OSName = 'Mac';
    return OSName;
  }

  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
  }
}
